import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailOutlined, LockOutlined, GithubOutlined } from '@ant-design/icons';
import { Button, Form, Input, Typography, Divider } from 'antd';
import { getGitHubOAuthAuthorizationURL } from '../../utils/accounts';
import { FORGOT_PASSWORD_ROUTE, SIGN_UP_ROUTE } from '../../config/constants';
import styles from './LoginForm.module.css';

// credentials for login form
export interface LoginCredentials {
  usernameOrEmail: string
  password: string
};

export interface LoginFormProps {
  handleLogin: (credentials: LoginCredentials) => Promise<void>
};

// form used on login page
const LoginForm = (props: LoginFormProps): JSX.Element | null => {
  // password input visibility state
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  // login call enabled/disabled state
  const [loginDisabled, setLoginDisabled] = useState<boolean>(false);

  // handle user login
  const handleLogin = (credentials: LoginCredentials) => {
    if (loginDisabled) {
      return;
    }

    setLoginDisabled(true);
    props.handleLogin(credentials).finally(
      () => setLoginDisabled(false)
    );
  };

  // redirect to authorization url
  const handleGitHubLogin = () => {
    window.location.href = getGitHubOAuthAuthorizationURL();
  };

  return (
    <Form
      name='basic'
      layout={'vertical'}
      initialValues={{ remember: true }}
      onFinish={handleLogin}
    >
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Username/Email
          </Typography.Text>
        }
        name='usernameOrEmail'
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter your username/email
              </Typography.Text>
            )
          }
        ]}
      >
        <Input
          name='usernameOrEmail'
          placeholder='Enter username/email'
          prefix={<MailOutlined />}
          disabled={loginDisabled}
        />
      </Form.Item>
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Password
          </Typography.Text>
        }
        name='password'
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter your password
              </Typography.Text>
            )
          }
        ]}
      >
        <Input.Password
          name='password'
          placeholder='Enter password'
          prefix={<LockOutlined />}
          visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
          disabled={loginDisabled}
        />
      </Form.Item>
      <Form.Item className={styles.forgotPasswordItem}>
        <Typography.Text
          className={styles.formLabelSmall}
        >
          <Link to={FORGOT_PASSWORD_ROUTE}>
            Forgot Password?
          </Link>
        </Typography.Text>
      </Form.Item>
      <Form.Item className={styles.submitButtonContainer}>
        <Button
          type='primary'
          htmlType='submit'
          loading={loginDisabled}
          className='mainButton'
        >
          Login
        </Button>
      </Form.Item>
      <Divider plain>
        <Typography.Text
          className={styles.formLabel}
        >
          Or login using
        </Typography.Text>
      </Divider>
      <Form.Item className={styles.gitHubOAuthButtonItem}>
        <Button
          type='primary'
          onClick={handleGitHubLogin}
          className={styles.gitHubOAuthButton}
        >
          <GithubOutlined />
          GitHub
        </Button>
      </Form.Item>
      <Form.Item className={styles.signUpTextItem}>
        <Typography.Text
          className={styles.formLabelSmall}
        >
          Don't have an account?&nbsp;
          <Link to={SIGN_UP_ROUTE}>
            Sign Up!
          </Link>
        </Typography.Text>
      </Form.Item>
    </Form>
  );
};

export default LoginForm;
